// What Foundation's CharacterSets and Swift's `Character` see, for porting SuiteModel string handling exactly.
// Swift compares and slices Strings by Character (extended grapheme cluster), Foundation's CharacterSet and
// `trimmingCharacters(in:)` work on Unicode scalars, NSString on UTF-16 (which JS strings already are).

/** `CharacterSet.whitespaces` as CoreFoundation implements it (CFUniChar.c `isWhitespace`): note U+200B is in and
 *  U+180E is out, whatever the documentation's "General Category Zs" says. */
export function isCFWhitespace(cp: number): boolean {
  return cp === 0x20 || cp === 0x09 || cp === 0xa0 || cp === 0x1680 || (cp >= 0x2000 && cp <= 0x200b) ||
    cp === 0x202f || cp === 0x205f || cp === 0x3000;
}

/** `CharacterSet.newlines` (CFUniChar.c `isNewline`). */
export function isCFNewline(cp: number): boolean {
  return (cp >= 0x0a && cp <= 0x0d) || cp === 0x85 || cp === 0x2028 || cp === 0x2029;
}

/** `CharacterSet.whitespacesAndNewlines`. */
export function isCFWhitespaceOrNewline(cp: number): boolean {
  return isCFWhitespace(cp) || isCFNewline(cp);
}

const ALPHANUMERIC = /[\p{L}\p{M}\p{N}]/u;

/** `CharacterSet.alphanumerics`: General Categories L*, M* and N* (Apple's documentation; CoreFoundation's bitmap is
 *  generated from them, at the Unicode version of the OS — this uses the JS engine's). */
export function isCFAlphanumeric(cp: number): boolean {
  if (cp < 0x80) return (cp >= 0x30 && cp <= 0x39) || (cp >= 0x41 && cp <= 0x5a) || (cp >= 0x61 && cp <= 0x7a);
  return ALPHANUMERIC.test(String.fromCodePoint(cp));
}

/** `trimmingCharacters(in: .whitespaces)`: scalar by scalar from both ends. */
export function trimCFWhitespace(s: string): string {
  return trimCF(s, isCFWhitespace);
}

/** `trimmingCharacters(in: .whitespacesAndNewlines)`. */
export function trimCFWhitespaceAndNewlines(s: string): string {
  return trimCF(s, isCFWhitespaceOrNewline);
}

function trimCF(s: string, drop: (cp: number) => boolean): string {
  let start = 0;
  let end = s.length;
  while (start < end) {
    const cp = s.codePointAt(start)!;
    if (!drop(cp)) break;
    start += cp > 0xffff ? 2 : 1;
  }
  while (end > start) {
    let cp = s.charCodeAt(end - 1);
    let width = 1;
    if (cp >= 0xdc00 && cp <= 0xdfff && end - 2 >= start) {
      const lead = s.charCodeAt(end - 2);
      if (lead >= 0xd800 && lead <= 0xdbff) { cp = ((lead - 0xd800) << 10) + (cp - 0xdc00) + 0x10000; width = 2; }
    }
    if (!drop(cp)) break;
    end -= width;
  }
  return s.slice(start, end);
}

// MARK: Grapheme clusters

const segmenter = new Intl.Segmenter(undefined, { granularity: 'grapheme' });
/** Text in which every UTF-16 unit is a cluster of its own: Latin-1 and Latin Extended (below the combining
 *  diacriticals), general punctuation other than ZWNJ/ZWJ, and no CR (CR LF is one cluster). */
const SIMPLE = /^[\u0000-\u000c\u000e-\u02ff\u2010-\u2064]*$/;

/** The string as Swift's `Array(string)`: one entry per Character. */
export function graphemes(s: string): string[] {
  if (SIMPLE.test(s)) return s.split('');
  const out: string[] = [];
  for (const { segment } of segmenter.segment(s)) out.push(segment);
  return out;
}

/** The first `n` Characters (fewer when the string is shorter), reading no further than needed. */
export function firstGraphemes(s: string, n: number): string[] {
  if (SIMPLE.test(s.slice(0, n + 1))) return s.slice(0, n).split('');
  const out: string[] = [];
  for (const { segment } of segmenter.segment(s)) {
    if (out.length === n) break;
    out.push(segment);
  }
  return out;
}

/** Characters that attach to the cluster before them (GB9, GB9a): Extend, ZWJ and SpacingMark (approximated by Mc
 *  plus the two Lo spacing marks). Enough to tell whether an ASCII character ends a cluster. */
const JOINS_PREVIOUS = /^[\p{Grapheme_Extend}\p{Emoji_Modifier}\u200d\p{Mc}\u0e33\u0eb3]/u;
/** Grapheme_Cluster_Break=Prepend (GB9b), which attaches to the cluster after it. */
const PREPEND = /[\u0600-\u0605\u06dd\u070f\u0890\u0891\u08e2\u0d4e\u{110bd}\u{110cd}\u{111c2}\u{111c3}\u{1193f}\u{11941}\u{11a3a}\u{11a84}-\u{11a89}\u{11d46}\u{11f02}]$/u;

function joinsPrevious(s: string, at: number): boolean {
  return at < s.length && JOINS_PREVIOUS.test(s.slice(at, at + 2));
}

/** Whether a cluster boundary falls at `at`, when an ASCII character sits on at least one side of it. */
export function asciiBoundary(s: string, at: number): boolean {
  if (at <= 0 || at >= s.length) return true;
  if (s[at - 1] === '\r' && s[at] === '\n') return false;
  if (joinsPrevious(s, at)) return false;
  return !PREPEND.test(s.slice(Math.max(0, at - 2), at));
}

/** `String.hasPrefix` for an ASCII `prefix`: the text starts with those Characters. */
export function hasPrefix(s: string, prefix: string): boolean {
  return s.startsWith(prefix) && asciiBoundary(s, prefix.length);
}

/** `String.hasSuffix` for an ASCII `suffix`. */
export function hasSuffix(s: string, suffix: string): boolean {
  return s.endsWith(suffix) && asciiBoundary(s, s.length - suffix.length);
}

/** `String.contains(_:)`/`range(of:)` for an ASCII `needle`, matching whole Characters only; UTF-16 index or -1. */
export function indexOf(s: string, needle: string, from = 0): number {
  let i = s.indexOf(needle, from);
  while (i >= 0) {
    if (asciiBoundary(s, i) && asciiBoundary(s, i + needle.length)) return i;
    i = s.indexOf(needle, i + 1);
  }
  return -1;
}

/** `split(separator: "\n")` keeping empties: "\r\n" is one Character, so its "\n" doesn't split. */
export function splitLines(s: string): string[] {
  return s.split(/(?<!\r)\n/);
}

const WHITE_SPACE = /^\p{White_Space}/u;
const ALPHABETIC = /^\p{Alphabetic}/u;
const NUMBER = /^\p{N}/u;

/** `Character.isWhitespace`: the first scalar has the White_Space property. */
export function charIsWhitespace(c: string): boolean { return WHITE_SPACE.test(c); }
/** `Character.isLetter`: the first scalar is Alphabetic. */
export function charIsLetter(c: string): boolean { return ALPHABETIC.test(c); }
/** `Character.isNumber`: the first scalar has a numeric type (N*; the Unihan numerals are letters anyway). */
export function charIsNumber(c: string): boolean { return NUMBER.test(c); }

/** Unicode scalars of a string (lone surrogates count as one, as Swift repairs them to U+FFFD). */
export function* scalars(s: string): Generator<number> {
  for (let i = 0; i < s.length; i++) {
    const cp = s.codePointAt(i)!;
    if (cp > 0xffff) i++;
    yield cp;
  }
}
